// Fork-only thin wrapper over the app's own locale files. Translation content
// lives in `excalidraw-app/locales/*.json`, loaded by `loadDrawolaLocale` in
// `excalidraw-app/locales/index.ts`.
//
// Why wrap:
//  - Upstream's `useI18n()` relies on jotai-scope `createIsolation` which
//    requires the calling component to live inside the `<Excalidraw>` tree.
//    Some storage components (e.g. BlockingQuotaModal) render at the app
//    root, outside that provider, so using `useI18n()` directly would throw
//    "Missing Provider from createIsolation".
//  - The app's own locale files load asynchronously, separately from
//    upstream's. `useDrawolaT()` subscribes to both: `useI18n()` for
//    upstream's load, and `drawolaLocaleVersionAtom` for ours.

import { useI18n } from "@excalidraw/excalidraw/i18n";

import { useAtomValue } from "../app-jotai";
import { drawolaLocaleVersionAtom, translateDrawola } from "../locales";

type Replacement = { [key: string]: string | number };

/** Translate a fork string from the app's own locale files. Works anywhere. */
export function drawolaT(key: string, replacement?: Replacement): string {
  return translateDrawola(key, replacement);
}

/**
 * Hook variant that subscribes to the current app langCode so the component
 * re-renders on language change. Returns the same function shape as
 * `drawolaT` for ergonomic use: `const t = useDrawolaT(); t("files.title")`.
 */
export function useDrawolaT(): (
  key: string,
  replacement?: Replacement,
) => string {
  // Subscribe via upstream `useI18n()` so re-renders fire AFTER
  // `setLanguage()` has finished its async `import("./locales/<code>.json")`
  // — i.e. at the same moment as every other upstream i18n consumer.
  //
  // IMPORTANT: `useI18n()` uses jotai-scope `createIsolation`, which means
  // this hook only works in components rendered inside the `<Excalidraw>`
  // tree. All current callers (AppMainMenu, FileListSidebar, StorageMeter,
  // BlockingQuotaModal) render inside it. If you add a caller at the app
  // root, either move it inside `<Excalidraw>` or take the translator as a
  // prop — the pattern `StorageMeterView` uses so the Studio (which renders
  // outside the tree, with `studio/i18n.ts` `useStudioT`) can share it.
  useI18n();
  // Also subscribe to the app locale version atom so re-renders fire when
  // `loadDrawolaLocale` finishes loading the app's own file. The two loads
  // (upstream's and ours) are separate async operations.
  useAtomValue(drawolaLocaleVersionAtom);
  return drawolaT;
}
